/**
 * サンドボックス上限設定カード
 *
 * 稼働時間・転送量の月あたりの上限を、利用者ごと・全体の 4 軸で設定する。
 * 上限に達すると新しい実行環境を確保できなくなるため、システム全体のキルスイッチ
 * でもある。コスト上限（`SystemCostLimitCard`）と同じく、入力確定と送信を分離した
 * 2 段階コミットとし、鉛筆アイコンからの即時保存は用いない。
 *
 * ## 入力は利用者が扱う単位で受け取る
 *
 * 保存する値は秒とバイトだが、画面では時間と GB で入出力する。運用側が判断する
 * 単位で書かせないと、桁を間違えたまま保存できてしまう。
 */
import type { SandboxUsageLimits } from '@kyumo/common';
import { Container, Loader2, Pencil, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  useDeleteSandboxLimits,
  useSandboxLimits,
  useSetSandboxLimits,
} from '../../hooks/use-admin-api';

const HOUR_SECONDS = 3600;
const GIGABYTE = 1024 ** 3;

type Stage = 'idle' | 'editing' | 'confirming-save' | 'confirming-reset';

/** 画面の入力値（時間・GB）。空文字を許すのは編集中の一時状態のため */
interface FormValues {
  perUserRuntimeHours: string;
  perUserTransferGb: string;
  systemRuntimeHours: string;
  systemTransferGb: string;
}

/**
 * 表示用の数値。**割り切れない値を整数へ丸めない。**
 * 丸めると、0.5 GB のような設定を開いただけで別の値へすり替わる。
 */
function toDisplayNumber(value: number): string {
  return String(Number(value.toFixed(3)));
}

function toForm(limits: SandboxUsageLimits): FormValues {
  return {
    perUserRuntimeHours: toDisplayNumber(limits.perUserRuntimeSeconds / HOUR_SECONDS),
    perUserTransferGb: toDisplayNumber(limits.perUserTransferBytes / GIGABYTE),
    systemRuntimeHours: toDisplayNumber(limits.systemRuntimeSeconds / HOUR_SECONDS),
    systemTransferGb: toDisplayNumber(limits.systemTransferBytes / GIGABYTE),
  };
}

/**
 * 0 より大きい数か。
 *
 * **小数を弾かない。** 0.5 時間・0.5 GB のような刻みで運用したい場面があり、
 * 整数だけに縛ると保存できない。保存値（秒・バイト）へ直す時点で整数へ丸める。
 */
function toPositiveNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

/**
 * 入力を保存値へ。1 つでも不正なら null（部分的に保存しない）
 *
 * `current` を渡すと、**表示が変わっていない項目は保存値をそのまま引き継ぐ**。
 * 表示は 3 桁で丸めるため（`toDisplayNumber`）、そこから戻した値は元の保存値と
 * 一致しない。触っていない項目まで戻して書くと、稼働時間だけを直して保存した
 * 管理者が、意図せず転送量の上限も数百 KB ずらすことになる
 */
function toLimits(form: FormValues, current?: SandboxUsageLimits): SandboxUsageLimits | null {
  const perUserRuntimeHours = toPositiveNumber(form.perUserRuntimeHours);
  const systemRuntimeHours = toPositiveNumber(form.systemRuntimeHours);
  const perUserTransferGb = toPositiveNumber(form.perUserTransferGb);
  const systemTransferGb = toPositiveNumber(form.systemTransferGb);
  if (
    perUserRuntimeHours === null ||
    systemRuntimeHours === null ||
    perUserTransferGb === null ||
    systemTransferGb === null
  ) {
    return null;
  }
  // 保存値は整数（秒・バイト）。API のスキーマが整数を要求するため、ここで丸める。
  // 1 秒・1 バイト未満へ潰れる入力は 0 になり得るので下限を 1 で止める
  const displayed = current ? toForm(current) : undefined;
  /** 表示が変わっていなければ元の保存値を使う（丸め戻しでずらさない） */
  const keep = (field: keyof FormValues, stored: number | undefined, next: number): number =>
    displayed && stored !== undefined && displayed[field] === form[field] ? stored : next;

  return {
    perUserRuntimeSeconds: keep(
      'perUserRuntimeHours',
      current?.perUserRuntimeSeconds,
      Math.max(1, Math.round(perUserRuntimeHours * HOUR_SECONDS)),
    ),
    systemRuntimeSeconds: keep(
      'systemRuntimeHours',
      current?.systemRuntimeSeconds,
      Math.max(1, Math.round(systemRuntimeHours * HOUR_SECONDS)),
    ),
    perUserTransferBytes: keep(
      'perUserTransferGb',
      current?.perUserTransferBytes,
      Math.max(1, Math.round(perUserTransferGb * GIGABYTE)),
    ),
    systemTransferBytes: keep(
      'systemTransferGb',
      current?.systemTransferBytes,
      Math.max(1, Math.round(systemTransferGb * GIGABYTE)),
    ),
  };
}

function LimitField({
  label,
  unit,
  value,
  onChange,
  disabled,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1">
        <input
          type="number"
          min={0}
          step="any"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 rounded-md border border-border bg-background px-2 py-1 text-right text-foreground"
          aria-label={label}
        />
        <span className="text-muted-foreground">{unit}</span>
      </span>
    </label>
  );
}

export function SandboxLimitsCard() {
  const { data, isLoading, isError, error, refetch } = useSandboxLimits();
  const setLimits = useSetSandboxLimits();
  const resetLimits = useDeleteSandboxLimits();

  const [stage, setStage] = useState<Stage>('idle');
  const [form, setForm] = useState<FormValues | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isMutating = setLimits.isPending || resetLimits.isPending;

  const startEditing = useCallback(() => {
    if (!data) return;
    setForm(toForm(data.limits));
    setErrorMessage(null);
    setStage('editing');
  }, [data]);

  const cancel = useCallback(() => {
    setForm(null);
    setErrorMessage(null);
    setStage('idle');
  }, []);

  const save = useCallback(async () => {
    if (!form) return;
    // 触っていない項目は保存済みの値を引き継ぐ（丸め戻しでずらさない）
    const limits = toLimits(form, data?.limits);
    if (!limits) {
      setErrorMessage('すべての項目に 0 より大きい数値を入力してください');
      setStage('editing');
      return;
    }
    try {
      await setLimits.mutateAsync(limits);
      cancel();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '保存に失敗しました');
      setStage('editing');
    }
  }, [form, data?.limits, setLimits, cancel]);

  const reset = useCallback(async () => {
    try {
      await resetLimits.mutateAsync();
      cancel();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '既定値へ戻せませんでした');
      setStage('idle');
    }
  }, [resetLimits, cancel]);

  // **一度でも失敗したら、読み直しの最中でも隠さない。** `isLoading` は値を
  // 持たない間の再取得でも真に戻るため、これだけで隠すと、下の失敗表示にある
  // 「既定値へ戻す」を利用者が押すたびにカードごと画面から消える。保存済みの値が
  // 壊れているときはそれが唯一の復旧手段であり、押した瞬間に消えては使えない
  if (isLoading && !isError) return null;

  // **読めなかったことを「無い」と区別して出す。** 読み込み中と同じく `null` を
  // 返すと、カードごと画面から消えて「この配備には無い機能」と見分けがつかない。
  // しかもこのカードは「既定値へ戻す」を持つ唯一の画面であり、保存済みの値が
  // 応答スキーマを満たさなくなった（0 が入った等）ときは、消えたカードのせいで
  // **その値を直す手段がアプリ内から一切無くなる**。だから失敗時も戻す操作は残す
  // **判定は「値が無いこと」で行う。** `isError` を混ぜると、再取得（窓の再フォーカス、
  // 一時的な 5xx）が失敗しただけでカードごと差し替わる。React Query は失敗しても
  // 直前の成功値を保持しているので、編集中なら入力途中の値が黙って消える。
  // 取得済みの値があるうちは通常の表示を保ち、失敗は下の注記だけで伝える
  if (!data) {
    return (
      <div
        className="bg-card rounded-xl p-6 border border-border"
        data-testid="sandbox-limits-card"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="p-3 rounded-lg bg-blue-500/20 text-blue-400">
            <Container className="w-6 h-6" />
          </div>
          <div>
            <p className="text-foreground font-semibold">サンドボックス上限</p>
            <p className="text-muted-foreground text-sm">
              現在の設定を読み込めませんでした
              {error instanceof Error && error.message ? `（${error.message}）` : ''}
            </p>
          </div>
        </div>

        {errorMessage && <p className="text-sm text-destructive mb-2">{errorMessage}</p>}

        {/*
          **ここでも確認を挟む。** 保存済みの上限を消す操作は取り消せず、
          しかもこの経路では現在値が読めていないため、何を捨てるのかを
          利用者も画面も把握していない。読み込みの一時的な失敗が、確認なしで
          組織全体の上限を消せる状態に変わってはならない。
          既定値は読めていないので、正常時のように具体値は示せない
        */}
        {stage === 'confirming-reset' ? (
          <Confirm
            message="保存済みの上限を消して既定値へ戻します。現在の設定を読み込めていないため、消える値は確認できません。"
            confirmLabel="既定値へ戻す"
            onConfirm={reset}
            onCancel={cancel}
            busy={isMutating}
          />
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refetch()}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground"
            >
              再読み込み
            </button>
            <button
              type="button"
              onClick={() => setStage('confirming-reset')}
              disabled={isMutating}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground disabled:opacity-50"
            >
              既定値へ戻す
            </button>
          </div>
        )}
      </div>
    );
  }

  const current = toForm(data.limits);
  const defaults = toForm(data.defaults);
  const editing = stage !== 'idle' && form !== null;
  // **確認中は値を凍結する。** 確認は「この値でよいか」を示す段であり、
  // 入力が開いたままだと、警告を読んでから値を変えて確定でき、
  // 保存されるのは警告が指していた値ではなくなる
  const fieldsDisabled = isMutating || stage === 'confirming-save';

  return (
    <div className="bg-card rounded-xl p-6 border border-border" data-testid="sandbox-limits-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-blue-500/20 text-blue-400">
            <Container className="w-6 h-6" />
          </div>
          <div>
            <p className="text-foreground font-semibold">サンドボックス上限</p>
            <p className="text-muted-foreground text-sm">
              月あたりの稼働時間と転送量。達すると新しい実行環境を確保できなくなります
            </p>
          </div>
        </div>
        {stage === 'idle' && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={startEditing}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
              aria-label="サンドボックス上限を編集"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {data.setting && (
              <button
                type="button"
                onClick={() => setStage('confirming-reset')}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                aria-label="既定値へ戻す"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {editing ? (
          <>
            <LimitField
              label="利用者ごとの稼働時間"
              unit="時間 / 月"
              value={form.perUserRuntimeHours}
              onChange={(v) => setForm({ ...form, perUserRuntimeHours: v })}
              disabled={fieldsDisabled}
            />
            <LimitField
              label="利用者ごとの転送量"
              unit="GB / 月"
              value={form.perUserTransferGb}
              onChange={(v) => setForm({ ...form, perUserTransferGb: v })}
              disabled={fieldsDisabled}
            />
            <LimitField
              label="全体の稼働時間"
              unit="時間 / 月"
              value={form.systemRuntimeHours}
              onChange={(v) => setForm({ ...form, systemRuntimeHours: v })}
              disabled={fieldsDisabled}
            />
            <LimitField
              label="全体の転送量"
              unit="GB / 月"
              value={form.systemTransferGb}
              onChange={(v) => setForm({ ...form, systemTransferGb: v })}
              disabled={fieldsDisabled}
            />
          </>
        ) : (
          <dl className="space-y-1 text-sm">
            <ValueRow
              label="利用者ごとの稼働時間"
              value={`${current.perUserRuntimeHours} 時間 / 月`}
            />
            <ValueRow label="利用者ごとの転送量" value={`${current.perUserTransferGb} GB / 月`} />
            <ValueRow label="全体の稼働時間" value={`${current.systemRuntimeHours} 時間 / 月`} />
            <ValueRow label="全体の転送量" value={`${current.systemTransferGb} GB / 月`} />
          </dl>
        )}

        <p className="text-xs text-muted-foreground pt-1">
          {data.setting
            ? `${new Date(data.setting.updatedAt).toLocaleString('ja-JP')} に ${data.setting.updatedBy} が変更（既定値: 利用者 ${defaults.perUserRuntimeHours} 時間 / ${defaults.perUserTransferGb} GB、全体 ${defaults.systemRuntimeHours} 時間 / ${defaults.systemTransferGb} GB）`
            : '既定値で動作しています'}
        </p>

        {/*
          再取得の失敗は表示を壊さずに伝える。値は直前の成功時のものが
          残っており、上限としても引き続き効いている
        */}
        {isError && (
          <p className="text-xs text-muted-foreground">
            最新の設定を読み込めませんでした。表示しているのは直前に取得した値です
          </p>
        )}

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        {stage === 'editing' && (
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={() => setStage('confirming-save')}
              disabled={isMutating}
              className="px-3 py-1.5 rounded-md bg-primary-500 text-sm text-white disabled:opacity-50"
            >
              保存する
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={isMutating}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground"
            >
              やめる
            </button>
          </div>
        )}

        {stage === 'confirming-save' && form && (
          <Confirm
            message={`上限を「利用者 ${form.perUserRuntimeHours} 時間 / ${form.perUserTransferGb} GB、全体 ${form.systemRuntimeHours} 時間 / ${form.systemTransferGb} GB」で保存します。上限に達している利用者は、新しい実行環境を確保できなくなります。`}
            confirmLabel="保存する"
            onConfirm={save}
            onCancel={() => setStage('editing')}
            busy={isMutating}
          />
        )}

        {stage === 'confirming-reset' && (
          <Confirm
            message={`上限を既定値（利用者 ${defaults.perUserRuntimeHours} 時間 / ${defaults.perUserTransferGb} GB、全体 ${defaults.systemRuntimeHours} 時間 / ${defaults.systemTransferGb} GB）へ戻します。`}
            confirmLabel="既定値へ戻す"
            onConfirm={reset}
            onCancel={cancel}
            busy={isMutating}
          />
        )}
      </div>
    </div>
  );
}

function ValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

function Confirm({
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  busy,
}: {
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
      <p className="text-sm text-foreground">{message}</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 text-sm text-white disabled:opacity-50"
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          {confirmLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground"
        >
          やめる
        </button>
      </div>
    </div>
  );
}
